import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customersAPI, userPreferencesAPI, tagsAPI, i18nSettingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { getAddressConfig } from '../lib/i18n-config';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, Loader2, Users, MapPin, Phone, MoreHorizontal, AlertTriangle, Upload, Download, History, Filter } from 'lucide-react';
import TagMultiSelect, { TagChip, TagChips } from '../components/TagMultiSelect';
import { toast } from 'sonner';
import { ListPagination } from '../components/ListPagination';
import { ColumnToggle } from '../components/ColumnToggle';
import { CRMImportModal, ImportHistoryModal } from '../components/CRMImportModal';
import { MobileFAB } from '../components/MobileFAB';

const CUSTOMERS_COLUMN_DEFS = [
  { key: 'name', label: 'Name' },
  { key: 'company', label: 'Company' },
  { key: 'address', label: 'Address' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'source', label: 'Source' },
  { key: 'last_service', label: 'Last Service' },
  { key: 'type', label: 'Type' },
];

function buildColumnState(defs, saved) {
  const state = {};
  for (const col of defs) {
    state[col.key] = { label: col.label, visible: saved ? saved[col.key] !== false : true };
  }
  return state;
}

const EMPTY_FORM = {
  first_name: '', last_name: '', company_name: '', customer_type: '',
  primary_phone: '', secondary_phone: '', billing_email: '', billing_phone: '',
  service_street: '', service_unit: '', service_city: '', service_state: '', service_zip: '',
  service_address_notes: '', latitude: null, longitude: null,
  customer_notes: '', tags: '', lead_source: '', do_not_service: false,
};

export default function Customers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isOffice } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [columns, setColumns] = useState(() => buildColumnState(CUSTOMERS_COLUMN_DEFS, null));
  const prefsFetched = useRef(false);
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [allCustomerTags, setAllCustomerTags] = useState([]);
  const [filterTags, setFilterTags] = useState([]);
  const [businessCountry, setBusinessCountry] = useState('US');
  const [serviceCountries, setServiceCountries] = useState(['US']);

  // Load tenant's business country for address config + service countries for
  // multi-country address autocomplete restriction.
  useEffect(() => {
    i18nSettingsAPI.get().then(res => {
      const primary = res.data?.business_country_code || 'US';
      setBusinessCountry(primary);
      const sc = res.data?.service_country_codes;
      setServiceCountries(Array.isArray(sc) && sc.length > 0 ? sc : [primary]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (prefsFetched.current) return;
    prefsFetched.current = true;
    userPreferencesAPI.get().then((res) => {
      const saved = res.data?.customers_table_columns;
      if (saved) setColumns(buildColumnState(CUSTOMERS_COLUMN_DEFS, saved));
    }).catch(() => {});
  }, []);

  const handleColumnToggle = useCallback((key, visible) => {
    setColumns((prev) => {
      const next = { ...prev, [key]: { ...prev[key], visible } };
      const payload = {};
      for (const k in next) payload[k] = next[k].visible;
      userPreferencesAPI.update({ customers_table_columns: payload }).catch(() =>
        toast.error('Failed to save column preference')
      );
      return next;
    });
  }, []);

  const handleColumnReset = useCallback(() => {
    const reset = buildColumnState(CUSTOMERS_COLUMN_DEFS, null);
    setColumns(reset);
    const payload = {};
    for (const k in reset) payload[k] = true;
    userPreferencesAPI.update({ customers_table_columns: payload }).catch(() =>
      toast.error('Failed to save column preference')
    );
  }, []);

  const isVisible = (key) => columns[key]?.visible !== false;
  const visibleColCount = Object.values(columns).filter((c) => c.visible !== false).length + 1; // +1 for actions

  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sort') || null;
  const sortDir = searchParams.get('dir') || null;
  const pageSize = 25;

  const handleSort = useCallback((key) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const curKey = p.get('sort');
      const curDir = p.get('dir');
      const DEFAULTS = { name: 'asc', company: 'asc', email: 'asc', phone: 'asc', source: 'asc', type: 'asc', last_service: 'desc', created: 'desc' };
      const defDir = DEFAULTS[key] || 'asc';
      if (curKey !== key) { p.set('sort', key); p.set('dir', defDir); }
      else if (curDir === defDir) { p.set('sort', key); p.set('dir', defDir === 'asc' ? 'desc' : 'asc'); }
      else { p.delete('sort'); p.delete('dir'); }
      p.set('page', '1');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (filterTags.length > 0) params.tag_ids = filterTags.map(t => t.id).join(',');
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      const res = await customersAPI.list(params);
      setCustomers(res.data.customers);
      setTotal(res.data.total);
    } catch (err) {
      if (err?.response?.status === 403 && err?.response?.data?.detail?.code === 'ENTITLEMENT_REQUIRED') {
        toast.error('CRM module is not enabled for your account.');
      } else {
        toast.error('Failed to load customers');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, filterTags, sortBy, sortDir]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  useEffect(() => {
    tagsAPI.list({ entity_type: 'customer' }).then(res => setAllCustomerTags(res.data || [])).catch(() => {});
  }, []);

  const handleSearch = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value) { params.set('search', value); } else { params.delete('search'); }
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleCreateTag = async (name, entityType) => {
    try {
      const res = await tagsAPI.create({ name, entity_type: entityType });
      const newTag = res.data;
      setAllCustomerTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      return newTag;
    } catch { return null; }
  };

  const handlePlaceSelect = useCallback((place) => {
    setForm(prev => ({ ...prev, ...place }));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() && !form.company_name.trim()) {
      toast.error('First name or company name is required');
      return;
    }
    if (!form.primary_phone.trim()) {
      toast.error('Primary phone is required');
      return;
    }
    if (!form.service_street.trim()) {
      toast.error('Service street address is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim() || null,
        last_name: form.last_name.trim() || null,
        company_name: form.company_name.trim() || null,
        customer_type: form.customer_type || null,
        primary_phone: form.primary_phone.trim(),
        secondary_phone: form.secondary_phone.trim() || null,
        billing_email: form.billing_email.trim() || null,
        billing_phone: form.billing_phone.trim() || null,
        service_street: form.service_street.trim(),
        service_unit: form.service_unit.trim() || null,
        service_city: form.service_city.trim() || null,
        service_state: form.service_state.trim() || null,
        service_zip: form.service_zip.trim() || null,
        service_address_notes: form.service_address_notes.trim() || null,
        latitude: form.latitude,
        longitude: form.longitude,
        customer_notes: form.customer_notes.trim() || null,
        tags: form.tags.trim() || null,
        lead_source: form.lead_source.trim() || null,
        do_not_service: form.do_not_service,
      };
      const res = await customersAPI.create(payload);
      toast.success(`Customer "${res.data.display_name}" created`);
      setDrawerOpen(false);
      setForm({ ...EMPTY_FORM });
      fetchCustomers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (customer) => {
    try {
      await customersAPI.update(customer.id, { is_active: !customer.is_active });
      toast.success(`Customer ${customer.is_active ? 'deactivated' : 'activated'}`);
      fetchCustomers();
    } catch { toast.error('Failed to update customer'); }
  };

  const totalPages = Math.ceil(total / pageSize);
  const isDirty = !!(form.first_name || form.last_name || form.company_name || form.primary_phone);

  const handleExport = async () => {
    try {
      const res = await customersAPI.exportCSV(search);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="customers-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="customers-title">
              <Users className="w-6 h-6" />
              Customers
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              {total} customer{total !== 1 ? 's' : ''} in your CRM
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(isOwner || isOffice) && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="import-export-menu-btn" className="hidden md:inline-flex">
                      <Upload className="w-4 h-4 mr-1" /> Import / Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setImportOpen(true)} data-testid="import-csv-btn">
                      <Upload className="w-4 h-4 mr-2" /> Import CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExport} data-testid="export-csv-btn">
                      <Download className="w-4 h-4 mr-2" /> Export CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setHistoryOpen(true)} data-testid="import-history-btn">
                      <History className="w-4 h-4 mr-2" /> Import History
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={() => setDrawerOpen(true)} data-testid="add-customer-btn" className="hidden md:inline-flex">
                  <Plus className="w-4 h-4 mr-2" /> Add Customer
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0 md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 h-10"
              data-testid="customer-search-input"
            />
          </div>
          {/* Desktop: tag filter + column toggle */}
          <div className="hidden md:flex items-center gap-2">
            <div className="w-[220px]">
              <TagMultiSelect
              entityType="customer"
              selectedTags={filterTags}
              onChange={(tags) => {
                setFilterTags(tags);
                const params = new URLSearchParams(searchParams);
                params.set('page', '1');
                setSearchParams(params);
              }}
              allTags={allCustomerTags}
              onCreateTag={handleCreateTag}
              placeholder="Filter by tags..."
              data-testid="customer-tag-filter"
            />
            </div>
          </div>
          <ColumnToggle columns={columns} onToggle={handleColumnToggle} onReset={handleColumnReset} />
        </div>

        {/* Active filter chips */}
        {filterTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="active-customer-filters">
            {filterTags.map(t => (
              <TagChip key={t.id} name={t.name} onRemove={() => setFilterTags(prev => prev.filter(x => x.id !== t.id))} />
            ))}
            <button
              className="text-xs text-muted-foreground hover:text-foreground underline"
              onClick={() => setFilterTags([])}
              data-testid="clear-customer-tag-filters"
            >
              Clear tags
            </button>
          </div>
        )}

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2" data-testid="customers-mobile-cards">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : customers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <Users className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">{search || filterTags.length ? 'No customers found' : 'No customers yet'}</p>
            </div>
          ) : customers.map(c => (
            <div
              key={c.id}
              className={`border rounded-lg p-3.5 active:bg-muted/50 ${!c.is_active ? 'opacity-60' : ''}`}
              onClick={() => navigate(`/customers/${c.id}`)}
              data-testid={`customer-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <TruncatedTextWithTooltip
                      text={c.display_name}
                      className="font-semibold text-sm"
                    />
                    {!c.is_active && <Badge variant="secondary" className="text-[10px] shrink-0">Inactive</Badge>}
                    {c.do_not_service && <Badge variant="destructive" className="text-[10px] shrink-0">DNS</Badge>}
                  </div>
                  {c.company_name && (
                    <TruncatedTextWithTooltip
                      as="p"
                      text={c.company_name}
                      className="text-xs text-muted-foreground"
                    />
                  )}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    {c.primary_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.primary_phone}</span>}
                    {c.service_city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.service_city}{c.service_state ? `, ${c.service_state}` : ''}</span>}
                  </div>
                </div>
                <MoreHorizontal className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="border rounded-lg overflow-x-auto hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                {isVisible('name') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="name" onSort={handleSort} className="min-w-[140px]">Name</SortableTableHead>}
                {isVisible('company') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="company" onSort={handleSort} className="min-w-[110px] hidden md:table-cell">Company</SortableTableHead>}
                {isVisible('address') && <TableHead className="min-w-[150px] hidden lg:table-cell">Address</TableHead>}
                {isVisible('phone') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="phone" onSort={handleSort} className="min-w-[110px] hidden md:table-cell">Phone</SortableTableHead>}
                {isVisible('email') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="email" onSort={handleSort} className="min-w-[130px] hidden lg:table-cell">Email</SortableTableHead>}
                {isVisible('source') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="source" onSort={handleSort} className="w-24 hidden xl:table-cell">Source</SortableTableHead>}
                {isVisible('last_service') && <TableHead className="w-28 hidden xl:table-cell">Last Service</TableHead>}
                {isVisible('type') && <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="type" onSort={handleSort} className="w-24 hidden md:table-cell">Type</SortableTableHead>}
                <TableHead className="text-right w-16">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-10 h-10 text-muted-foreground/40" />
                      <p className="text-sm font-medium">{search || filterTags.length ? 'No customers found' : 'No customers yet'}</p>
                      <p className="text-xs text-muted-foreground">{search || filterTags.length ? 'Try adjusting your filters.' : 'Add your first customer to get started.'}</p>
                      {!search && !filterTags.length && (isOwner || isOffice) && (
                        <Button size="sm" className="mt-2" onClick={() => setDrawerOpen(true)} data-testid="empty-add-customer-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add Customer
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((c) => (
                  <TableRow
                    key={c.id}
                    className={`cursor-pointer hover:bg-muted/50 ${!c.is_active ? 'opacity-60' : ''}`}
                    onClick={() => navigate(`/customers/${c.id}`)}
                    data-testid={`customer-row-${c.id}`}
                  >
                    {isVisible('name') && (
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <TruncatedTextWithTooltip
                            text={c.display_name}
                            className="font-semibold text-sm max-w-[160px]"
                            data-testid={`customer-name-${c.id}`}
                          />
                          {!c.is_active && <Badge variant="secondary" className="text-[10px] flex-shrink-0">Inactive</Badge>}
                          {c.do_not_service && (
                            <Badge variant="destructive" className="text-[10px] flex-shrink-0 flex items-center gap-0.5">
                              <AlertTriangle className="w-3 h-3" /> DNS
                            </Badge>
                          )}
                        </div>
                        {c.tag_objects && c.tag_objects.length > 0 && (
                          <div className="mt-0.5"><TagChips tags={c.tag_objects} /></div>
                        )}
                      </TableCell>
                    )}
                    {isVisible('company') && (
                      <TableCell className="hidden md:table-cell">
                        <TruncatedTextWithTooltip
                          text={c.company_name || '—'}
                          className="text-sm block max-w-[120px]"
                          data-testid={`customer-company-${c.id}`}
                        />
                      </TableCell>
                    )}
                    {isVisible('address') && (
                      <TableCell className="hidden lg:table-cell">
                        <TruncatedTextWithTooltip
                          text={[c.service_street, c.service_city, c.service_state].filter(Boolean).join(', ') || '—'}
                          className="text-xs text-muted-foreground block max-w-[180px]"
                          data-testid={`customer-address-${c.id}`}
                        />
                      </TableCell>
                    )}
                    {isVisible('phone') && (
                      <TableCell className="hidden md:table-cell">
                        <span className="text-sm" data-testid={`customer-phone-${c.id}`}>{c.primary_phone || '—'}</span>
                      </TableCell>
                    )}
                    {isVisible('email') && (
                      <TableCell className="hidden lg:table-cell">
                        <TruncatedTextWithTooltip
                          text={c.billing_email || '—'}
                          className="text-sm block max-w-[150px]"
                          data-testid={`customer-email-${c.id}`}
                        />
                      </TableCell>
                    )}
                    {isVisible('source') && (
                      <TableCell className="hidden xl:table-cell">
                        <span className="text-xs text-muted-foreground" data-testid={`customer-source-${c.id}`}>{c.lead_source || '—'}</span>
                      </TableCell>
                    )}
                    {isVisible('last_service') && (
                      <TableCell className="hidden xl:table-cell">
                        <span className="text-sm" data-testid={`customer-last-svc-${c.id}`}>
                          {c.last_service_date ? new Date(c.last_service_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </span>
                      </TableCell>
                    )}
                    {isVisible('type') && (
                      <TableCell className="hidden md:table-cell">
                        {c.customer_type ? (
                          <Badge variant="outline" className="text-xs capitalize" data-testid={`customer-type-${c.id}`}>{c.customer_type}</Badge>
                        ) : <span className="text-sm text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" data-testid={`customer-menu-${c.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem onClick={() => navigate(`/customers/${c.id}`)} data-testid={`view-customer-${c.id}`}>
                            View Profile
                          </DropdownMenuItem>
                          {(isOwner || isOffice) && (
                            <DropdownMenuItem onClick={() => handleDeactivate(c)} data-testid={`toggle-active-${c.id}`}>
                              {c.is_active ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <ListPagination 
            page={page} 
            pageSize={pageSize} 
            total={total} 
            totalPages={totalPages} 
            showPageSize={false}
            onPageChange={handlePageChange} 
          />
        )}

        {/* Create Customer Drawer */}
        <AppDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title="New Customer"
          description="Add a new customer to your CRM."
          isDirty={isDirty}
          data-testid="create-customer-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => { setDrawerOpen(false); setForm({ ...EMPTY_FORM }); }}>Cancel</Button>
              <Button type="submit" form="create-customer-form" disabled={saving} data-testid="save-customer-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Customer
              </Button>
            </>
          }
        >
          <CustomerForm
            formId="create-customer-form"
            form={form}
            setForm={setForm}
            onSubmit={handleCreate}
            onPlaceSelect={handlePlaceSelect}
            businessCountry={businessCountry}
            serviceCountries={serviceCountries}
          />
        </AppDrawer>

        <CRMImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          entityType="customers"
          onImportComplete={fetchCustomers}
        />
        <ImportHistoryModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="customers"
          onRollbackComplete={fetchCustomers}
        />
      </div>
      <MobileFAB onClick={() => setDrawerOpen(true)} data-testid="mobile-fab-customer" />
    </Layout>
  );
}

function CustomerForm({ formId, form, setForm, onSubmit, onPlaceSelect, businessCountry = 'US', serviceCountries = null }) {
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const addrCfg = getAddressConfig(businessCountry);

  return (
    <form id={formId} onSubmit={onSubmit} className="space-y-5">
      {/* Customer Type */}
      <div>
        <Label>Customer Type</Label>
        <Select value={form.customer_type} onValueChange={(v) => update('customer_type', v)}>
          <SelectTrigger data-testid="select-customer-type">
            <SelectValue placeholder="Select type..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="residential">Residential</SelectItem>
            <SelectItem value="business">Business</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Name Section */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Name *</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="first_name">First Name</Label>
            <Input id="first_name" value={form.first_name} onChange={(e) => update('first_name', e.target.value)} placeholder="John" data-testid="input-first-name" />
          </div>
          <div>
            <Label htmlFor="last_name">Last Name</Label>
            <Input id="last_name" value={form.last_name} onChange={(e) => update('last_name', e.target.value)} placeholder="Doe" data-testid="input-last-name" />
          </div>
        </div>
        <div>
          <Label htmlFor="company_name">Company Name</Label>
          <Input id="company_name" value={form.company_name} onChange={(e) => update('company_name', e.target.value)} placeholder="Acme Corporation" data-testid="input-company-name" />
        </div>
        <p className="text-xs text-muted-foreground">First name or company name is required.</p>
      </div>

      {/* Contact Info */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Contact</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="primary_phone">Phone *</Label>
            <Input id="primary_phone" value={form.primary_phone} onChange={(e) => update('primary_phone', e.target.value)} placeholder="555-0100" data-testid="input-primary-phone" />
          </div>
          <div>
            <Label htmlFor="secondary_phone">Alt Phone</Label>
            <Input id="secondary_phone" value={form.secondary_phone} onChange={(e) => update('secondary_phone', e.target.value)} placeholder="555-0200" data-testid="input-secondary-phone" />
          </div>
        </div>
        <div>
          <Label htmlFor="billing_email">Email</Label>
          <Input id="billing_email" type="email" value={form.billing_email} onChange={(e) => update('billing_email', e.target.value)} placeholder="john@acme.com" data-testid="input-billing-email" />
        </div>
      </div>

      {/* Service Address */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Service Address *</p>
        <div>
          <Label htmlFor="service_street">{addrCfg.fields.street.label} *</Label>
          <AddressAutocomplete
            id="service_street"
            value={form.service_street}
            onChange={(v) => update('service_street', v)}
            onPlaceSelect={onPlaceSelect}
            placeholder={addrCfg.fields.street.placeholder}
            countryCode={addrCfg.regionCode}
            countryCodes={(serviceCountries || []).map(c => c.toLowerCase())}
            data-testid="input-service-street"
          />
        </div>
        <div>
          <Label htmlFor="service_unit">{addrCfg.fields.unit.label}</Label>
          <Input id="service_unit" value={form.service_unit} onChange={(e) => update('service_unit', e.target.value)} placeholder={addrCfg.fields.unit.placeholder} data-testid="input-service-unit" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="service_city">{addrCfg.fields.city.label}</Label>
            <Input id="service_city" value={form.service_city} onChange={(e) => update('service_city', e.target.value)} placeholder={addrCfg.fields.city.placeholder} data-testid="input-service-city" />
          </div>
          <div>
            <Label htmlFor="service_state">{addrCfg.fields.state.label}</Label>
            <Input id="service_state" value={form.service_state} onChange={(e) => update('service_state', e.target.value)} placeholder={addrCfg.fields.state.placeholder} data-testid="input-service-state" />
          </div>
          <div>
            <Label htmlFor="service_zip">{addrCfg.fields.zip.label}</Label>
            <Input id="service_zip" value={form.service_zip} onChange={(e) => update('service_zip', e.target.value)} placeholder={addrCfg.fields.zip.placeholder} data-testid="input-service-zip" />
          </div>
        </div>
        <div>
          <Label htmlFor="service_address_notes">Address Notes</Label>
          <Input id="service_address_notes" value={form.service_address_notes} onChange={(e) => update('service_address_notes', e.target.value)} placeholder="Gate code, parking instructions..." data-testid="input-service-address-notes" />
        </div>
        {form.latitude && form.longitude && (
          <p className="text-xs text-muted-foreground">Coordinates: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}</p>
        )}
      </div>

      {/* Metadata */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-muted-foreground">Metadata</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="tags">Tags</Label>
            <Input id="tags" value={form.tags} onChange={(e) => update('tags', e.target.value)} placeholder="vip, priority" data-testid="input-tags" />
            <p className="text-xs text-muted-foreground mt-1">Comma-separated</p>
          </div>
          <div>
            <Label htmlFor="lead_source">Lead Source</Label>
            <Input id="lead_source" value={form.lead_source} onChange={(e) => update('lead_source', e.target.value)} placeholder="Referral, Google..." data-testid="input-lead-source" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={form.do_not_service}
            onCheckedChange={(v) => update('do_not_service', v)}
            data-testid="switch-do-not-service"
          />
          <Label className="flex items-center gap-1.5 text-destructive">
            <AlertTriangle className="w-4 h-4" /> Do Not Service
          </Label>
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label htmlFor="customer_notes">Notes</Label>
        <Textarea
          id="customer_notes"
          value={form.customer_notes}
          onChange={(e) => update('customer_notes', e.target.value)}
          placeholder="Internal notes about this customer..."
          rows={3}
          data-testid="input-customer-notes"
        />
      </div>
    </form>
  );
}
